import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { Button, CircularProgress, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import debounce from "lodash.debounce";
import SnackBar from "./SnackBar";
import optionsParser from "../helpers/optionsParser";
import { api_get, api_post } from "../utils/Api";

const CodesToBeInvoicedAddDialog = (props) => {
  const { initializeData } = props;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackBar, setSnackBar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const defaultFilters = useMemo(() => ({ type: "cash", invoiced: 0 }), []);

  const fetchCashPayments = useMemo(
    () =>
      debounce((term) => {
        const sort = { accessor: "id", order: "desc" };
        const query = optionsParser(term, defaultFilters, sort, ["reference"]);
        setLoading(true);
        api_get(`codes?page=1${query}&perPage=20`)
          .then((resp) => {
            const payload = resp?.payload;
            setOptions(Array.isArray(payload) ? payload : []);
          })
          .catch(() => {
            setOptions([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 400),
    [defaultFilters]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchCashPayments(search);
    return () => {
      fetchCashPayments.cancel();
    };
  }, [open, search, fetchCashPayments]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setOpen(false);
    setSearch("");
    setOptions([]);
    setSelected([]);
  };

  const handleSubmit = () => {
    const ids = (selected || []).map((c) => c?.id).filter((v) => v !== undefined && v !== null);
    if (ids.length === 0) {
      setSnackBar({
        open: true,
        severity: "warning",
        message: "Please select at least one cash payment",
      });
      return;
    }

    setSubmitting(true);
    api_post("codes-to-be-invoiced", { codes: ids })
      .then((resp) => {
        if (resp?.code !== 200 && resp?.code !== 201) {
          setSnackBar({
            open: true,
            severity: "warning",
            message: resp?.message || "Failed to add codes",
          });
          return;
        }
        setSnackBar({
          open: true,
          severity: "success",
          message: "Added successfully",
        });
        handleClose();
        if (typeof initializeData === "function") {
          initializeData();
        }
      })
      .catch(() => {
        setSnackBar({
          open: true,
          severity: "error",
          message: "Failed to add codes",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <Button color="primary" variant="contained" onClick={handleOpen}>
        Add
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Add cash payments to be invoiced</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={options}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(option) =>
              `${option?.reference || option?.id || ""} - ${option?.user?.full_name || option?.user?.name || ""}`
            }
            filterSelectedOptions
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search cash payments (reference)"
                variant="outlined"
                margin="normal"
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snackBar.open}
        severity={snackBar.severity}
        message={snackBar.message}
        handleClose={() => setSnackBar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default CodesToBeInvoicedAddDialog;
